import type { Component, ConfigState, DesignOrientation } from '@renderer/types/configState'

const DEFAULT_WELCOME_ICON_PATH = '/assets/images/dreambooth-icon.png'
const DEFAULT_WELCOME_TEXT = 'Welcome to Dreambooth'

/**
 * True when a welcome list is still the untouched new-project template
 * (Dreambooth icon + "Welcome to Dreambooth" + one button). The button's
 * label and styling are ignored — they vary between template versions.
 */
export function isUntouchedDefaultWelcome(list: Component[] | undefined): boolean {
  if (!Array.isArray(list) || list.length !== 3) return false
  const types = list.map((c) => c.type).sort()
  if (types.join(',') !== 'button,image,text') return false
  const image = list.find((c) => c.type === 'image')
  const text = list.find((c) => c.type === 'text')
  return (
    typeof image?.src === 'string' &&
    image.src.endsWith(DEFAULT_WELCOME_ICON_PATH) &&
    text?.text === DEFAULT_WELCOME_TEXT
  )
}

/**
 * Picks the welcome design for the device orientation, and the orientation
 * whose canvas it was laid out on (the caller sizes the canvas from it).
 *
 * Prefers non-empty lists; `??` would keep an empty oriented branch and hide
 * legacy `components`. When the device's orientation still holds the default
 * template but the other orientation was designed, the designed one wins —
 * otherwise a portrait booth shows the template while the owner only ever
 * edited (and saw thumbnails of) the landscape design.
 */
export function resolveWelcomeDesign(
  welcome: ConfigState['pages']['welcome'] | undefined,
  orientation: DesignOrientation
): { components: Component[] | undefined; orientation: DesignOrientation } {
  if (!welcome) return { components: undefined, orientation }
  const oc = welcome.orientedComponents
  const other: DesignOrientation = orientation === 'portrait' ? 'landscape' : 'portrait'
  const own = oc?.[orientation]
  const theirs = oc?.[other]
  const hasOwn = Array.isArray(own) && own.length > 0
  const hasTheirs = Array.isArray(theirs) && theirs.length > 0

  if (hasOwn && !(isUntouchedDefaultWelcome(own) && hasTheirs && !isUntouchedDefaultWelcome(theirs))) {
    return { components: own, orientation }
  }
  if (hasTheirs) return { components: theirs, orientation: other }
  if (Array.isArray(welcome.components) && welcome.components.length > 0) {
    return { components: welcome.components, orientation }
  }
  return { components: undefined, orientation }
}
